/**
 * 바닥으로 **천천히** 내려간다 (2026-09-11 사용자 요청).
 *
 * ## 왜 `scrollIntoView({behavior:'smooth'})` 가 아닌가
 *
 * 브라우저의 smooth 는 **길이를 정할 수 없고** 크롬에서는 꽤 빠르다 — 「살짝 천천히」가 안 된다.
 * 그리고 스트리밍 중에는 **글자가 오는 내내 다시 시작**돼 덜컹거린다.
 *
 * 그래서 직접 그린다:
 *   · 길이를 정할 수 있다(기본 450ms)
 *   · ★★ **매 프레임 목표를 다시 읽는다** — 내려가는 동안 답이 자라도 바닥을 놓치지 않는다
 *   · 돌려주는 함수로 **중간에 멈출 수 있다**(다음 말이 붙으면 앞엣것을 멈추고 새로 시작)
 *
 * ★ `prefers-reduced-motion` 은 **부르는 쪽**이 본다 — 여기서는 「어떻게 움직이나」만 안다.
 */

/** 끝에서 잦아드는 곡선 — 처음엔 빠르고 끝이 부드럽다. */
export function easeOutCubic(t: number): number {
  const x = Math.min(Math.max(t, 0), 1);
  return 1 - (1 - x) ** 3;
}

/** 이 요소를 담고 있는 **구르는 상자**를 찾는다 — 화면마다 그 상자가 다르다. */
export function scrollParentOf(node: HTMLElement | null): HTMLElement | null {
  let el: HTMLElement | null = node?.parentElement ?? null;
  while (el) {
    const style = typeof getComputedStyle === 'function' ? getComputedStyle(el) : null;
    const oy = style?.overflowY ?? '';
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return el;
    el = el.parentElement;
  }
  return null;
}

/** 기본 길이 — 「살짝 천천히」. 너무 길면 답을 읽으려는 사람을 기다리게 한다. */
export const SCROLL_TO_BOTTOM_MS = 450;

/**
 * `el` 을 바닥까지 굴린다. **멈추는 함수**를 돌려준다.
 *
 * ★ 이미 바닥이면 아무것도 안 한다 — 없는 움직임을 만들지 않는다.
 */
export function animateScrollToBottom(
  el: HTMLElement,
  durationMs: number = SCROLL_TO_BOTTOM_MS,
  /** 다 내려갔을 때 알려 준다 — 부르는 쪽이 「아직 가는 중인가」를 알아야 하기 때문이다. */
  onDone?: () => void,
): () => void {
  const from = el.scrollTop;
  const target0 = el.scrollHeight - el.clientHeight;
  if (durationMs <= 0 || target0 - from <= 1) {
    el.scrollTop = Math.max(target0, 0);
    onDone?.();
    return () => {};
  }

  const startedAt = typeof performance === 'object' ? performance.now() : Date.now();
  let handle = 0;
  let stopped = false;

  const step = (): void => {
    if (stopped) return;
    const now = typeof performance === 'object' ? performance.now() : Date.now();
    const t = (now - startedAt) / durationMs;
    /* ★★ 목표를 «지금» 다시 읽는다 — 내려가는 동안 답이 길어져도 바닥을 따라간다. */
    const target = el.scrollHeight - el.clientHeight;
    if (t >= 1) { el.scrollTop = target; onDone?.(); return; }
    el.scrollTop = from + (target - from) * easeOutCubic(t);
    handle = requestAnimationFrame(step);
  };
  handle = requestAnimationFrame(step);

  return () => {
    stopped = true;
    if (handle) cancelAnimationFrame(handle);
  };
}

/** 「움직임을 줄여 달라」고 해 둔 사람인가 — 그러면 애니메이션 없이 곧장 간다. */
export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch { return false; }
}
